(function () {
  'use strict';

  /**
   * 站内商品搜索埋点扩展。
   *
   * 依赖：
   * sensors-click-tracking.js 必须先加载，并提供 window.jiniuTracking。
   *
   * 上报规则：
   * 1. 用户通过站内搜索表单或搜索联想词发起搜索时，先把搜索信息写入 sessionStorage。
   * 2. 浏览器进入 Shopify 搜索结果页并取得结果数量后，再上报一次 search 事件。
   * 3. 上报后立即删除暂存信息，刷新结果页或直接打开搜索链接不会重复上报。
   *
   * search_type：
   * - input：输入关键词后提交表单。
   * - suggestion：点击搜索联想词。
   *
   * search_position：
   * - header：桌面端页头搜索框。
   * - mobile_menu：移动端菜单或底部栏打开的搜索框。
   * - search_page：搜索结果页中的搜索框。
   */
  const tracking = window.jiniuTracking;

  if (!tracking) {
    console.error('[SensorsData] Tracking core is unavailable');
    return;
  }

  const EVENT_NAME = 'search';
  const INPUT_SEARCH_TYPE = 'input';
  const SUGGESTION_SEARCH_TYPE = 'suggestion';
  const PENDING_SEARCH_KEY = '_jiniu_pending_search';
  const PENDING_SEARCH_TTL = 10 * 60 * 1000;
  const SEARCH_POSITION_ATTRIBUTE = 'data-jiniu-search-position';
  const QUERY_SUGGESTION_SELECTOR =
    '.predictive-search-list-item-query a[href]';

  function getContext() {
    return window.jiniuTrackingContext || {};
  }

  function normalizeText(value) {
    return value === null || value === undefined
      ? ''
      : String(value).trim();
  }

  function normalizeKeyword(value) {
    return normalizeText(value).replace(/\s+/g, ' ').toLowerCase();
  }

  function getSearchInput(form) {
    return form?.querySelector('input[type="search"][name="q"]') || null;
  }

  function isSearchForm(form) {
    if (!(form instanceof HTMLFormElement) || !getSearchInput(form)) {
      return false;
    }

    try {
      const action = new URL(form.action, window.location.origin);
      const searchUrl = new URL(
        getContext().searchUrl || '/search',
        window.location.origin
      );

      return action.pathname === searchUrl.pathname;
    } catch (error) {
      return false;
    }
  }

  function getSearchPosition(form) {
    return (
      form.getAttribute(SEARCH_POSITION_ATTRIBUTE) ||
      form
        .closest(`[${SEARCH_POSITION_ATTRIBUTE}]`)
        ?.getAttribute(SEARCH_POSITION_ATTRIBUTE) ||
      ''
    );
  }

  function setPendingSearch(value) {
    try {
      window.sessionStorage.setItem(
        PENDING_SEARCH_KEY,
        JSON.stringify(value)
      );
      return true;
    } catch (error) {
      console.error('[SensorsData] Unable to store pending search', error);
      return false;
    }
  }

  function getPendingSearch() {
    let storedValue = '';

    try {
      storedValue = window.sessionStorage.getItem(PENDING_SEARCH_KEY) || '';
    } catch (error) {
      return null;
    }

    if (!storedValue) {
      return null;
    }

    try {
      const pendingSearch = JSON.parse(storedValue);

      if (
        !pendingSearch ||
        typeof pendingSearch !== 'object' ||
        !pendingSearch.searchKeyword ||
        !pendingSearch.createdAt ||
        Date.now() - pendingSearch.createdAt > PENDING_SEARCH_TTL
      ) {
        removePendingSearch();
        return null;
      }

      return pendingSearch;
    } catch (error) {
      removePendingSearch();
      return null;
    }
  }

  function removePendingSearch() {
    try {
      window.sessionStorage.removeItem(PENDING_SEARCH_KEY);
    } catch (error) {
      // sessionStorage may be unavailable in privacy-restricted browsers.
    }
  }

  function saveSearchIntent(
    form,
    searchType,
    searchKeyword,
    suggestionText
  ) {
    const normalizedKeyword = normalizeText(searchKeyword);
    const searchPosition = getSearchPosition(form);

    if (!normalizedKeyword || !searchPosition) {
      return;
    }

    setPendingSearch({
      searchKeyword: normalizedKeyword,
      searchType,
      searchPosition,
      suggestionText: normalizeText(suggestionText),
      createdAt: Date.now()
    });
  }

  function getSuggestionKeyword(anchor, form) {
    try {
      const destination = new URL(
        anchor.getAttribute('href'),
        window.location.origin
      );
      const keyword = destination.searchParams.get('q');

      if (normalizeText(keyword)) {
        return keyword;
      }
    } catch (error) {
      // Fall back to the current search input below.
    }

    return getSearchInput(form)?.value || '';
  }

  function scheduleAfterEvent(callback) {
    if (typeof window.queueMicrotask === 'function') {
      window.queueMicrotask(callback);
    } else {
      Promise.resolve().then(callback);
    }
  }

  /**
   * 表单自己的校验监听器会先执行；延迟到当前事件结束后再判断
   * defaultPrevented，避免记录空关键词或被页面阻止的提交。
   */
  document.addEventListener('submit', function (event) {
    const form = event.target;

    if (!isSearchForm(form)) {
      return;
    }

    scheduleAfterEvent(() => {
      if (event.defaultPrevented) {
        return;
      }

      saveSearchIntent(
        form,
        INPUT_SEARCH_TYPE,
        getSearchInput(form)?.value,
        ''
      );
    });
  });

  /**
   * 只统计 Shopify query 联想词。
   * 商品和集合预测结果不在这里上报 search；商品点击继续由
   * sensors-product-click-tracking.js 上报 product_click。
   */
  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const anchor = event.target.closest(QUERY_SUGGESTION_SELECTOR);

    if (!anchor) {
      return;
    }

    const form = anchor.closest('predictive-search')?.querySelector('form');

    if (!isSearchForm(form)) {
      return;
    }

    scheduleAfterEvent(() => {
      if (event.defaultPrevented) {
        return;
      }

      const suggestionText = normalizeText(
        anchor.querySelector('.predictive-search-item-heading')?.textContent ||
          anchor.textContent
      );

      saveSearchIntent(
        form,
        SUGGESTION_SEARCH_TYPE,
        getSuggestionKeyword(anchor, form),
        suggestionText
      );
    });
  });

  function publishCompletedSearch() {
    const context = getContext();

    if (context.pageType !== 'search' || context.searchPerformed !== true) {
      return;
    }

    const pendingSearch = getPendingSearch();

    if (!pendingSearch) {
      return;
    }

    const completedKeyword =
      normalizeText(context.searchTerms) ||
      normalizeText(new URLSearchParams(window.location.search).get('q'));

    if (
      normalizeKeyword(completedKeyword) !==
      normalizeKeyword(pendingSearch.searchKeyword)
    ) {
      removePendingSearch();
      return;
    }

    const numericResultCount = Number(context.searchResultCount);
    const properties = {
      search_keyword: completedKeyword,
      search_type: pendingSearch.searchType,
      search_position: pendingSearch.searchPosition,
      search_category: '',
      suggestion_text: pendingSearch.suggestionText || '',
      result_count: Number.isFinite(numericResultCount)
        ? numericResultCount
        : 0
    };

    // 先删除再上报，确保页面刷新不会重复发送同一次搜索。
    removePendingSearch();
    tracking.publish(EVENT_NAME, properties);
  }

  publishCompletedSearch();
})();
